'use client';

import React, { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useInViewportAnimation } from '@/hooks/useInViewportAnimation';

// HowBuy 数据项接口定义
interface HowBuyItem {
  word1: string;
  word2: string;
}

// BlueLightHowBuy 组件接口定义
interface BlueLightHowBuyProps {
  howBuyData: {
    title?: string;
    words?: HowBuyItem[];
  };
  deviceType?: EPlatform;
  isFromCache?: boolean;
  onSectionExp?: (params: any) => void;
}

export default function BlueLightHowBuy({
  howBuyData,
  deviceType,
  isFromCache = false,
  onSectionExp,
}: BlueLightHowBuyProps) {
  const howBuyRef = useRef<HTMLDivElement>(null);
  const isMobile = deviceType !== EPlatform.WEB;

  // 使用 useInView 进行模块曝光检测
  const [expRef, inView] = useInView({
    threshold: 0.5, // 50%可见时触发
    triggerOnce: true, // 只触发一次
  });

  // isFromCache 现在通过参数传入，无需检测

  // PC端动画 - 使用useInViewportAnimation
  const animationRef = useInViewportAnimation(
    () => {
      if (isFromCache || isMobile) return;

      try {
        // 标题动画
        const h2 = document.getElementsByClassName('how-buy-h2');
        if (h2[0]) {
          h2[0].classList.add('activityFadeInUpAnimate');
        }

        // 列表项动画
        const listItems = document.getElementsByClassName('how-buy-li');
        Array.from(listItems).forEach((el) => {
          el.classList.add('activityFadeInUpAnimate');
        });
      } catch {
        // 出错时直接显示
        const elements = document.querySelectorAll('.blue-light-how-buy .opacity0');
        elements.forEach((el) => {
          (el as HTMLElement).style.opacity = '1';
        });
      }
    },
    { threshold: 0.3, once: true },
  );

  // 模块曝光埋点处理
  useEffect(() => {
    if (inView && onSectionExp) {
      onSectionExp({
        mkt_name: '如何购买',
      });
    }
  }, [inView, onSectionExp]);

  if (!howBuyData?.words || howBuyData.words.length === 0) {
    return null;
  }

  return (
    <div
      ref={(node) => {
        // 为埋点设置ref
        expRef(node);
        // 为其他功能设置ref
        if (isMobile) {
          howBuyRef.current = node;
        } else if (animationRef && typeof animationRef !== 'function') {
          (animationRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }}
      className={`blue-light-how-buy ${
        isMobile
          ? 'bg-[#F5F6F7] text-[#333333] py-[25px] px-[8px]'
          : 'bg-transparent text-white pb-[40px]'
      }`}
    >
      {/* 标题 */}
      <h2
        className={`title text-center font-bs how-buy-h2 ${
          isMobile
            ? 'text-[20px] leading-[30px] mb-[20px] text-[#333333]'
            : 'text-[24px] mb-[40px] xl:text-[36px] xl:mb-[40px] 2xl:text-[48px] 2xl:mb-[40px] opacity0'
        }`}
        style={
          !isMobile
            ? {
                background: 'linear-gradient(90deg, #FFFFFF 0%, #A1BCFF 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }
            : undefined
        }
      >
        {howBuyData.title}
      </h2>

      {isMobile ? (
        // 移动端布局 - 边框列表
        <ul className="list-none p-0 m-0 rounded-md border border-[#89909A] p-[16px] mx-[8px] bg-transparent">
          {howBuyData.words?.map((item, index) => (
            <li key={index} className="pb-5 last:pb-0 text-[#333333]">
              <span className="font-bs text-[13px]">{item.word1}&nbsp;:&nbsp;&nbsp;</span>
              <span className="text-[11px]">{item.word2}</span>
            </li>
          ))}
        </ul>
      ) : (
        // PC端布局 - 3列grid布局
        <ul className="list-none p-0 m-0 gap-[20px] grid grid-cols-3 text-white px-[6.25vw]">
          {howBuyData.words?.map((item, index) => (
            <li
              key={index}
              className="flex items-center justify-center w-full h-[60px] xl:h-[70px] 2xl:h-[80px] bg-transparent border border-[#E5E5E5] rounded-xl px-[6px] xl:px-[8px] 2xl:px-[10px] mb-[20px] how-buy-li opacity0"
            >
              <span className="font-bs text-[14px] xl:text-[18px] 2xl:text-[24px] min-w-[fit-content]">
                {item.word1}&nbsp;:&nbsp;&nbsp;
              </span>
              <span className="text-[12px] xl:text-[14px] 2xl:text-[16px]">{item.word2}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
